import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, errMsg, getToken } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { payWithRazorpay } from "../lib/razorpay";
import { RechargeBox } from "../components/RechargeCredits";
import { toast } from "sonner";
import {
  CreditCard, Download, Loader2, CheckCircle2, XCircle, Clock,
  IndianRupee, CalendarDays, Ban,
} from "lucide-react";

const STATUS_BADGE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  created: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-red-50 text-red-600 border-red-200",
  refunded: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Billing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [gst, setGst] = useState(18);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [payingPlan, setPayingPlan] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autopay, setAutopay] = useState(null);
  const [enablingAutopay, setEnablingAutopay] = useState(false);

  async function loadAll() {
    try {
      const [p, c, h, a] = await Promise.all([
        api.get("/payments/plans"),
        api.get("/subscriptions/current"),
        api.get("/payments/history"),
        api.get("/payments/autopay/status"),
      ]);
      setPlans(p.data.plans || []);
      setGst(p.data.gst_percent ?? 18);
      setCurrent(c.data);
      setHistory(h.data.payments || []);
      setAutopay(a.data);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load billing info"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); }, []);

  async function buy(plan) {
    setPayingPlan(plan.plan_id);
    try {
      const res = await payWithRazorpay({ plan_id: plan.plan_id, billing_cycle: cycle });
      toast.success("Payment successful!");
      navigate(`/payment/success?pid=${res.payment_id}&inv=${res.invoice_number || ""}&amount=${res.amount}&label=${encodeURIComponent(res.label || "")}`);
    } catch (e) {
      const msg = e?.message || errMsg(e, "Payment failed");
      if (msg !== "Payment cancelled") navigate(`/payment/failed?reason=${encodeURIComponent(msg)}`);
      else toast.message("Payment cancelled");
    } finally {
      setPayingPlan(null);
    }
  }

  async function cancelSub() {
    if (!current?.subscription) return;
    if (!window.confirm("Cancel subscription? Access continues till the current period ends.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/subscriptions/${current.subscription.id}/cancel`);
      toast.success(`Cancelled — access until ${(data.access_until || "").slice(0, 10)}`);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e, "Cancel failed"));
    } finally {
      setCancelling(false);
    }
  }

  function downloadInvoice(p) {
    const url = `${API}/payments/${p.id}/invoice`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => { if (!r.ok) throw new Error("Invoice not available"); return r.blob(); })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${p.invoice_number || p.id}.pdf`;
        a.click();
      })
      .catch((e) => toast.error(e.message));
  }

  async function enableAutopayFlow() {
    setEnablingAutopay(true);
    try {
      const { enableAutoPay } = await import("../lib/razorpay");
      const res = await enableAutoPay();
      toast.success(res.message || "Auto-Pay activated!");
      loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Auto-Pay setup failed";
      if (msg !== "Auto-Pay setup cancelled") toast.error(msg);
    } finally {
      setEnablingAutopay(false);
    }
  }

  const sub = current?.subscription;

  return (
    <AppShell role="seller">
      <div className="max-w-5xl space-y-8" data-testid="billing-page">
        <div>
          <h1 className="text-2xl font-bold text-[#282C3F]">Billing &amp; Payments</h1>
          <p className="text-sm text-[#535665]">Manage your plan, payments and invoices</p>
        </div>

        {/* Auto-Pay */}
        <div
          className="rounded-xl p-5 border"
          style={autopay?.autopay_active
            ? { background: "#F0FDF4", borderColor: "#BBF7D0" }
            : { background: "linear-gradient(135deg, #FFF7ED 0%, #FFF1F5 100%)", borderColor: "#FFD3B5" }}
          data-testid="autopay-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: autopay?.autopay_active ? "#059669" : "#EA580C" }}>
                {autopay?.autopay_active ? "Auto-Pay Active ✓" : "Auto-Pay"}
              </div>
              <div className="text-lg font-bold text-[#282C3F] mt-0.5">
                {autopay?.autopay_active
                  ? "₹999 auto-deducts every month — 100 listings + Data Analysis"
                  : "Never run out of credits — ₹999/month auto-deducted"}
              </div>
              <div className="text-sm text-[#535665] mt-1">
                {autopay?.autopay_active
                  ? "Cancel anytime from the subscription above — no further charges after cancelling."
                  : "One-time authorization (UPI Autopay / card mandate). Every month: 100 AI listings + full Data Analysis, automatically."}
              </div>
            </div>
            {!autopay?.autopay_active && (
              <button
                onClick={enableAutopayFlow}
                disabled={enablingAutopay}
                className="px-5 py-3 rounded-xl text-sm font-bold disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)", color: "#FFFFFF", boxShadow: "0 10px 24px -8px rgba(255,63,108,0.5)" }}
                data-testid="enable-autopay-btn"
              >
                {enablingAutopay ? "Opening Razorpay…" : "Enable Auto-Pay ₹999/mo"}
              </button>
            )}
          </div>
        </div>

        {/* Current plan */}
        <div className="bg-white border border-[#EDEDF1] rounded-xl p-5" data-testid="current-plan-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#94969F]">Current Plan</div>
              <div className="text-xl font-bold text-[#282C3F] capitalize">
                {sub?.plan_name || current?.plan_tier || "—"}
                {sub && <span className="ml-2 text-xs font-medium text-[#03A685]">({sub.billing_cycle})</span>}
              </div>
              <div className="text-sm text-[#535665] mt-1">
                Status: <span className="font-semibold capitalize">{current?.subscription_status || "—"}</span>
                {current?.subscription_expires_at && (
                  <> · Renews/expires: {String(current.subscription_expires_at).slice(0, 10)}</>
                )}
              </div>
              {typeof current?.listings_credits === "number" && (
                <div className="text-sm text-[#535665]">
                  Listing credits: {Math.max(0, (current.listings_credits || 0) - (current.listings_credits_used || 0))} left
                </div>
              )}
            </div>
            {sub && sub.status === "active" && (
              <button
                onClick={cancelSub}
                disabled={cancelling}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-red-200 text-red-600 text-sm hover:bg-red-50"
                data-testid="cancel-subscription-btn"
              >
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                Cancel subscription
              </button>
            )}
          </div>
        </div>

        {/* Instant credits recharge */}
        <div className="bg-white border-2 border-[#03A685] rounded-xl p-5" data-testid="billing-recharge-card">
          <div className="font-bold text-[#282C3F]">Instant Listings Recharge</div>
          <div className="text-xs text-[#94969F] mb-4">₹10 = 1 AI Auto Listing · pay any amount, credits activate instantly</div>
          <RechargeBox onSuccess={() => loadAll()} />
        </div>

        {/* Payment history */}
        <div className="bg-white border border-[#EDEDF1] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#EDEDF1] font-bold text-[#282C3F]">Payment History</div>
          {loading ? (
            <div className="p-8 text-center text-sm text-[#94969F]">Loading…</div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#94969F]" data-testid="no-payments">No payments yet</div>
          ) : (
            <table className="w-full text-sm" data-testid="payment-history-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[#94969F] border-b border-[#EDEDF1]">
                  <th className="px-5 py-2">Date</th>
                  <th className="px-3 py-2">Plan / Service</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Payment ID</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id} className="border-b border-[#F5F5F8]">
                    <td className="px-5 py-2 text-[#535665]">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-[#282C3F]">{p.label || p.service_name || "—"}</td>
                    <td className="px-3 py-2 font-semibold">₹{(p.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-xs text-[#94969F]">{p.razorpay_payment_id || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] border capitalize ${STATUS_BADGE[p.status] || STATUS_BADGE.created}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {p.status === "paid" && (
                        <button onClick={() => downloadInvoice(p)}
                          className="flex items-center gap-1 text-xs text-[#FF3F6C] hover:underline"
                          data-testid={`invoice-btn-${p.id}`}>
                          <Download size={12} /> PDF
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  );
}
